import React from 'react';

import {ConnectorSizing, HingeDomainVisualization, SharedDomainParams} from 'antibodies/AntibodyTypedefs';
import {assert} from 'Asserts';

import AntibodyComponentPopover from './AntibodyComponentPopover';

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export type AntibodyHingeProps = SharedDomainParams & {
  /* The color of the hinge */
  color: string;
  domainVisualization: HingeDomainVisualization;
  connectorSizing: ConnectorSizing;
};

/**
 * A longer, vertical line connected to a shorter line with a joint.
 * This is an SVG component, so all distance units are 'abstract user units', relative to the SVG viewbox.
 * @param domainVisualization The visualization for the hinge domain
 * @param color The color of the line
 * @param proteinId The API ID of the protein entity associated with this domain. If passed, the hinge will be
 *                  given a SmartChip-style popover with metadata about the protein.
 * @param connectorSizing The sizing params for this format's connectors
 */
export default function AntibodyHinge({
  domainVisualization,
  color,
  proteinId,
  inaccessible,
  label,
  showPopovers,
  connectorSizing,
}: AntibodyHingeProps): JSX.Element {
  const {angle, fixedEndLength, freeEndLength} = domainVisualization;
  const {strokeThickness} = connectorSizing;
  assert(angle >= 0 && angle <= 180, 'Hinge must be between 0 and 180 degrees');
  // Do an AAS law-of-sines calculation on a right triangle abutting the hinge. freeEndLength is the
  // hypotenuse.
  let jointX: number;
  let jointY: number;
  if (angle === 90) {
    // Easy case, no trigonometry required.
    jointX = freeEndLength;
    jointY = 0;
  } else if (angle < 90) {
    // Use the triangle between the short part of the hinge and the long part of the hinge.
    const alpha = degreesToRadians(angle);
    const beta = degreesToRadians(90 - angle);
    const gamma = degreesToRadians(90);
    jointX = freeEndLength * (Math.sin(alpha) / Math.sin(gamma));
    jointY = freeEndLength * (Math.sin(beta) / Math.sin(gamma));
  } else {
    // Use the triangle on top of the short part of the hinge.
    const acuteAngle = 180 - angle;
    const alpha = degreesToRadians(90 - acuteAngle);
    const beta = degreesToRadians(acuteAngle);
    const gamma = degreesToRadians(90);
    jointX = freeEndLength * (Math.sin(beta) / Math.sin(gamma));
    jointY = freeEndLength * (Math.sin(alpha) / Math.sin(gamma));
  }
  // Start from the outer end of the short part of the hinge. If this is an acute angle, this is below the top
  // of the path bounding box; if this is an obtuse angle, this is at the top of the path bounding box.
  const start = `M 0 ${angle >= 90 ? 0 : jointY}`;
  // Travel to the joint. In an acute angle, this is at the top of the path bounding box; if this is an obtuse
  // angle, this is below the top of the path bounding box.
  const toJoint = `L ${jointX} ${angle >= 90 ? jointY : 0}`;
  const toBottom = `L ${jointX} ${jointY + fixedEndLength}`;
  return (
    <AntibodyComponentPopover
      proteinId={proteinId}
      inaccessible={inaccessible}
      componentLabel={label}
      showPopovers={showPopovers}
    >
      <path
        d={`${start} ${toJoint} ${toBottom}`}
        stroke={color}
        strokeWidth={strokeThickness}
        fill="none"
        strokeLinejoin="round"
      />
    </AntibodyComponentPopover>
  );
}
